import React, { useState, useEffect } from 'react';
import { FaTimes, FaExclamationTriangle, FaCalendarAlt, FaGraduationCap, FaBullhorn } from 'react-icons/fa';

const AnnouncementBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);

  // Sample announcements - in a real app, these would come from an API
  const announcements = [
    {
      id: 1,
      type: 'urgent',
      icon: FaExclamationTriangle,
      title: 'Urgent Notice',
      message: 'School will be closed on Friday, August 15th for staff training.',
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-500'
    },
    {
      id: 2,
      type: 'admissions',
      icon: FaGraduationCap,
      title: 'Admissions Open',
      message: 'Applications for the 2024-2025 academic year are now open. Limited spots available!',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-500'
    },
    {
      id: 3,
      type: 'calendar',
      icon: FaCalendarAlt,
      title: 'New Term Dates',
      message: 'Second term begins on September 2nd, 2024. Check the events page for full calendar.',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-500'
    },
    {
      id: 4,
      type: 'news',
      icon: FaBullhorn,
      title: 'Sports Day Success',
      message: 'Congratulations to our students for winning the regional athletics championship!',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      iconColor: 'text-purple-500'
    }
  ];

  // Auto-rotate announcements every 8 seconds
  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Store dismissal in localStorage to remember user preference
    localStorage.setItem('announcementDismissed', 'true');
  };

  // Check if user has dismissed the banner
  useEffect(() => {
    const dismissed = localStorage.getItem('announcementDismissed');
    if (dismissed === 'true') {
      setIsVisible(false);
    }
  }, []);

  if (!isVisible || announcements.length === 0) return null;

  const announcement = announcements[currentAnnouncement];

  return (
    <div className={`${announcement.bgColor} ${announcement.borderColor} border-b relative z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          {/* Announcement Content */}
          <div className="flex items-center space-x-3 flex-1">
            <announcement.icon className={`w-5 h-5 ${announcement.iconColor} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span className={`font-semibold ${announcement.textColor} text-sm`}>
                  {announcement.title}
                </span>
                {announcements.length > 1 && (
                  <div className="flex space-x-1">
                    {announcements.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentAnnouncement(index)}
                        className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                          index === currentAnnouncement 
                            ? announcement.iconColor 
                            : 'bg-gray-300'
                        }`}
                        aria-label={`Go to announcement ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <p className={`${announcement.textColor} text-sm mt-1`}>
                {announcement.message}
              </p>
            </div>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className={`ml-4 p-1 rounded-md ${announcement.textColor} hover:bg-${announcement.color}-100 transition-colors duration-200`}
            aria-label="Dismiss announcement"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner; 